import React, { useState } from "react";
import { Box, Typography, Button, IconButton, FormControl, Select, MenuItem, Collapse, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import {
  useGetPublicHolidayListQuery,
  useAddPublicHolidayMutation,
  useUpdateBulkPublicHolidayMutation,
  useDeletePublicHolidayMutation,
} from "../api/financialApiSlice";
import CSVBulkUpdate from "../UserSettings/CSVBulkUpdate";

const LABEL_SX = {
  fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.08em",
  textTransform: "uppercase", color: "rgba(255,255,255,0.3)", mb: "5px",
};

const INFO_TOOLTIP_SX = {
  tooltip: {
    sx: {
      bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px",
      p: "12px 14px", maxWidth: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    },
  },
  arrow: { sx: { color: "#1C232C" } },
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117", borderRadius: "6px", fontSize: "0.84rem", color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "7px 10px" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)" },
};

const SELECT_SX = {
  backgroundColor: "#0d1117", borderRadius: "6px", fontSize: "0.84rem", color: "#e0e6f0",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.22)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB940 !important" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.4)" },
  "& .MuiSelect-select": { padding: "7px 10px" },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px",
      "& .MuiMenuItem-root": {
        fontSize: "0.84rem", color: "#e0e6f0",
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)" },
      },
    },
  },
};

export default function PublicHoliday() {
  const { data: holiday_data, isSuccess, refetch } = useGetPublicHolidayListQuery();
  const [addHoliday, { isSuccess: isAddSuccess }] = useAddPublicHolidayMutation();
  const [updateBulkPublicHoliday, { isSuccess: isUpdateSuccess, isError: isUpdateError }] = useUpdateBulkPublicHolidayMutation();

  const [bulkArray, setBulkArray] = useState([]);
  const [saved, setSaved] = useState("Saved!");
  const [deletingId, setDeletingId] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  React.useEffect(() => { if (isSuccess) setBulkArray(holiday_data); }, [isSuccess, holiday_data]);
  React.useEffect(() => { if (isAddSuccess) refetch(); }, [refetch, isAddSuccess]);
  React.useEffect(() => {
    if (isUpdateSuccess) setSaved("Saved!");
    else if (isUpdateError) setSaved("Save");
  }, [isUpdateSuccess, isUpdateError]);

  const handleAdd = () => {
    addHoliday({ body: { holiday_date: dayjs().format("YYYY-MM-DD"), day_of_week: "Saturday" } });
  };

  const handleSave = () => {
    updateBulkPublicHoliday({ body: bulkArray });
  };

  if (!isSuccess) return null;

  return (
    <Box sx={{ backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "16px", py: "12px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <CalendarTodayIcon sx={{ fontSize: "1rem", color: "#FDB940", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>Public Holidays</Typography>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#fff", mb: "4px" }}>Public Holidays</Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                Days that override the standard weekday/weekend ToU mapping. The day-of-week selector controls which weekend rates apply.
              </Typography>
            </Box>
          }
          placement="bottom"
          arrow
          componentsProps={INFO_TOOLTIP_SX}
        >
          <InfoOutlinedIcon sx={{ fontSize: "14px", color: "rgba(255,255,255,0.3)", cursor: "help", transition: "color 0.18s", "&:hover": { color: "#FDB940" } }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={handleAdd} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <AddIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <IconButton size="small" onClick={() => setCsvModalOpen(true)} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <UploadFileIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Rows */}
      <Box sx={{ p: "12px", flexGrow: 1 }}>
        {holiday_data?.map((holiday, i) => (
          <HolidayRow
            key={holiday.id}
            holiday={holiday}
            index={i}
            bulkArray={bulkArray}
            setBulkArray={setBulkArray}
            setSaved={setSaved}
            deletingId={deletingId}
            setDeletingId={setDeletingId}
          />
        ))}
        {holiday_data?.length === 0 && (
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.3)", textAlign: "center", py: "16px" }}>
            No public holidays. Click + to add one.
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: "16px", py: "10px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          disabled={saved !== "Save"}
          onClick={handleSave}
          sx={{
            bgcolor: "#FDB940", color: "#0d1117", fontWeight: 600, fontSize: "0.78rem",
            textTransform: "none", borderRadius: "8px", boxShadow: "none", px: "16px", py: "5px",
            "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          {saved}
        </Button>
      </Box>

      <CSVBulkUpdate
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        csvType="public-holidays"
        onSuccess={() => { refetch(); setSaved("Save"); }}
      />
    </Box>
  );
}

function HolidayRow({ holiday, index, bulkArray, setBulkArray, setSaved, deletingId, setDeletingId }) {
  const [deletePublicHoliday] = useDeletePublicHolidayMutation();
  const [holidayDate, setHolidayDate] = useState(dayjs(holiday.holiday_date));
  const [dayOfWeek, setDayOfWeek] = useState(holiday.day_of_week);

  const update = (field, value) => {
    setBulkArray((prev) => prev.map((item) => item.id === holiday.id ? { ...item, [field]: value } : item));
    setSaved("Save");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", px: "14px", py: "10px", backgroundColor: "#161B22", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px" }}>
          <Typography sx={{ width: 22, fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", fontWeight: 600, lineHeight: "32px", flexShrink: 0 }}>
            #{index + 1}
          </Typography>
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Date</Typography>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={holidayDate}
              onChange={(date) => {
                const d = date.set("hour", 0).set("minute", 0).set("second", 0);
                setHolidayDate(d);
                update("holiday_date", d.format("YYYY-MM-DD"));
              }}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Day of Week</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={dayOfWeek}
                onChange={(e) => { setDayOfWeek(e.target.value); update("day_of_week", e.target.value); }}
                sx={SELECT_SX}
                MenuProps={MENU_PROPS}
              >
                {["Saturday", "Sunday"].map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <IconButton
            size="small"
            onClick={() => setDeletingId(holiday.id)}
            sx={{ color: "rgba(255,255,255,0.3)", "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" }, flexShrink: 0 }}
          >
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Collapse in={deletingId === holiday.id}>
          <Box sx={{ mt: "3px", px: "12px", py: "8px", backgroundColor: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)" }}>Delete this holiday?</Typography>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Button size="small" onClick={() => setDeletingId(null)} sx={{ fontSize: "0.75rem", textTransform: "none", color: "rgba(255,255,255,0.5)" }}>Cancel</Button>
              <Button size="small" onClick={() => { deletePublicHoliday({ holiday_id: holiday.id }); setDeletingId(null); }}
                sx={{ fontSize: "0.75rem", textTransform: "none", color: "#f87171", border: "1px solid rgba(248,113,113,0.35)", borderRadius: "5px", px: "8px", "&:hover": { bgcolor: "rgba(248,113,113,0.1)" } }}>
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
